import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { optimizeSupabaseImage } from '@/lib/utils/imageOptimization';

interface PopupMessageProps {
  open: boolean;
  onClose: () => void;
  message: {
    id: string;
    title: string;
    body: string;
    message_type: string;
    icon: string;
    button_text: string;
    button_action?: string | null;
    cover_image?: string | null;
  };
  onAction?: (action: string) => void;
}

const TYPE_STYLES: Record<string, { bg: string; border: string; glow: string }> = {
  celebration: {
    bg: 'bg-gradient-to-br from-yellow-500/10 to-orange-500/10',
    border: 'border-yellow-500/30',
    glow: 'shadow-yellow-500/20',
  },
  milestone: {
    bg: 'bg-gradient-to-br from-purple-500/10 to-pink-500/10',
    border: 'border-purple-500/30',
    glow: 'shadow-purple-500/20',
  },
  encouragement: {
    bg: 'bg-gradient-to-br from-primary/10 to-primary/15',
    border: 'border-primary/30',
    glow: 'shadow-primary/20',
  },
  support: {
    bg: 'bg-gradient-to-br from-blue-500/10 to-cyan-500/10',
    border: 'border-blue-500/30',
    glow: 'shadow-blue-500/20',
  },
  tip: {
    bg: 'bg-gradient-to-br from-sky-500/10 to-blue-500/10',
    border: 'border-sky-500/30',
    glow: 'shadow-sky-500/20',
  },
  reengagement: {
    bg: 'bg-gradient-to-br from-orange-500/10 to-red-500/10',
    border: 'border-orange-500/30',
    glow: 'shadow-orange-500/20',
  },
};

export function PopupMessage({ open, onClose, message, onAction }: PopupMessageProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showConfetti, setShowConfetti] = useState(false);
  const styles = TYPE_STYLES[message.message_type] || TYPE_STYLES.encouragement;

  useEffect(() => {
    if (open && (message.message_type === 'celebration' || message.message_type === 'milestone')) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [open, message.message_type]);

  const handleButtonClick = () => {
    if (message.button_action) {
      if (message.button_action.startsWith('http')) {
        window.open(message.button_action, '_blank');
      } else if (message.button_action.startsWith('/')) {
        navigate(message.button_action);
      } else {
        onAction?.(message.button_action);
      }
    }
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent 
        className={cn(
          'sm:max-w-md border-2 shadow-lg',
          styles.bg,
          styles.border,
          styles.glow
        )}
      >
        {/* Confetti effect for celebrations */}
        {showConfetti && (
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute animate-bounce"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 0.5}s`,
                  animationDuration: `${0.5 + Math.random() * 0.5}s`,
                }}
              >
                {['🎉', '🎊', '✨', '⭐'][Math.floor(Math.random() * 4)]}
              </div>
            ))}
          </div>
        )}

        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-xl">
            <span className="text-4xl animate-bounce">{message.icon}</span>
            <span>{message.title}</span>
          </DialogTitle>
        </DialogHeader>

        {message.cover_image && (
          <img
            src={optimizeSupabaseImage(message.cover_image, { width: 600, format: 'webp' })}
            alt=""
            className="w-full h-40 object-cover rounded-lg"
            loading="lazy"
          />
        )}

        <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed">
          {message.body}
        </p>

        <div className="flex justify-end pt-2">
          <Button onClick={handleButtonClick} size="lg">
            {message.button_text || t('common.popupmessage.got_it', 'Got it!')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
